import React, { useState, useEffect } from 'react';
import { AlertCircle, X, ExternalLink } from 'lucide-react';
import { 
  getExtensionStoreUrl, 
  isEdgeBrowser
} from '../../utils/extensionUtils';
import { useTranslation } from 'react-i18next';
import logger from '../../utils/logger';

export const ExtensionNotification: React.FC = () => {
  const [isDismissed, setIsDismissed] = useState<boolean>(false);
  const { t } = useTranslation();

  // Check if the user has permanently dismissed this notification
  useEffect(() => {
    const dismissed = localStorage.getItem('extension_notification_dismissed');
    if (dismissed === 'true') {
      setIsDismissed(true);
    }
  }, []);

  const handleDismiss = () => {
    setIsDismissed(true);
    // We don't set in localStorage here - only if user permanently dismisses
  };

  const handlePermanentDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem('extension_notification_dismissed', 'true');
    logger.debug('Extension notification permanently dismissed');
  };

  const browserName = isEdgeBrowser() ? 'Microsoft Edge' : 'Chrome';
  const storeUrl = getExtensionStoreUrl();

  // Don't show if dismissed
  if (isDismissed) {
    return null;
  }

  return (
    <div className="relative mb-3 p-3 border border-blue-300 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 rounded-lg shadow-sm">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <AlertCircle className="h-5 w-5 text-blue-500 dark:text-blue-400" />
        </div>
        <div className="ml-3 w-full pr-8">
          <p className="text-sm text-blue-700 dark:text-blue-300 font-medium">
            {t('extensions.notInstalledMessage', { browserName })}
          </p>
          <div className="mt-2 flex space-x-3">
            <a
              href={storeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
              {t('extensions.installBrowserExtension')}
            </a>
            <button
              type="button"
              onClick={handlePermanentDismiss}
              className="inline-flex items-center px-3 py-1.5 border border-blue-300 dark:border-blue-700 text-xs font-medium rounded-md bg-white dark:bg-gray-800 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {t('extensions.dontShowAgain')}
            </button>
          </div>
        </div>
      </div>
      <button
        type="button"
        className="absolute top-2 right-2 text-blue-500 hover:text-blue-600 dark:hover:text-blue-400"
        onClick={handleDismiss}
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}; 